import { useEffect, useMemo, useState, type CSSProperties } from 'react';

/** Local size default preserved so no call site changes. */
const chipStyle = (selected: boolean) => sharedChipStyle(selected, 'md');

import { symbolTileLabelStyle, symbolTileStyle } from '../components/floor/tile-styles.js';
import { useAuth } from '../lib/use-auth.js';
import { useI18n } from '../lib/use-i18n.js';
import { useTrackedSnapshot } from '../lib/use-tracked-snapshot.js';
import { useActiveOutlet } from '../lib/use-active-outlet.js';
import { logger } from '../lib/logger.js';
import { addOnsRepoFor, ticketsRepoFor } from '../lib/repositories/outlet-repos.js';
import { voucherTemplatesRepoFor } from '../lib/repositories/vouchers-repo.js';
import { promotionsRepoFor } from '../lib/repositories/promotions-repo.js';
import { subscriptionsByCustomerSnapshot } from '../lib/repositories/wash-packages-repo.js';
import {
  BankIcon,
  CardIcon,
  CashIcon,
  CheckIcon,
  GiftIcon,
  PhoneIcon,
  QrIcon,
  Skeleton,
  Spinner,
  TagIcon,
  celebrateOnce,
  chipStyle as sharedChipStyle,
  feel,
  haptic,
  srOnly as posSrOnly,
} from '@kilat/ui';
import {
  callAdvanceTicket,
  callAppendItemsToTicket,
  callRecordPayment,
} from '../lib/callables/queue.js';
import {
  callGetTicketCustomerWallet,
  callIssueVoucher,
  type GetTicketCustomerWalletOutput,
} from '../lib/callables/loyalty.js';
import { callRedeemWashFromSubscription } from '../lib/callables/subscriptions.js';
import {
  checkRedemption,
  filterForOutlet,
  money,
  priceWithDiscounts,
  type AddOn,
  type CustomerSubscription,
  type IssuedVoucher,
  type PaymentMethod,
  type Promotion,
  type Tier,
  type Ticket,
  type VoucherTemplate,
} from '@kilat/shared';

/**
 * /pos — Cashier flow.
 *
 * Sprint 5:
 *   - Lists tickets in `readyForCollection` for the active outlet (live).
 *   - Tap a ticket → opens the payment sheet (method chips + amount + confirm).
 *   - On confirm → callRecordPayment → ticket archives + customer push fires.
 *
 * Sprint 6+: voucher picker, promo auto-apply preview, tip handling, refund.
 */

const PAYMENT_METHODS: readonly PaymentMethod[] = [
  'cash',
  'duitnow_qr',
  'tng',
  'grabpay',
  'card',
  'bank_transfer',
  'voucher',
  'free',
];

/**
 * One symbol per payment method, so the till reads without reading: a note for
 * cash, a QR for DuitNow, a phone for the e-wallets, a card for card. Many
 * cashiers on a Malaysian carwash floor read none of the app's four languages
 * (owner, 2026-10-16: "symbol is priority"); the label stays underneath.
 */
const METHOD_ICON: Record<PaymentMethod, (p: { size?: number }) => React.ReactNode> = {
  cash: CashIcon,
  duitnow_qr: QrIcon,
  tng: PhoneIcon,
  grabpay: PhoneIcon,
  card: CardIcon,
  bank_transfer: BankIcon,
  voucher: TagIcon,
  free: GiftIcon,
};

export function PosPage() {
  const { claims } = useAuth();
  const t = useI18n();
  const tenantId =
    typeof claims?.['defaultTenant'] === 'string' ? (claims['defaultTenant'] as string) : null;

  const { outletId, loading: outletLoading } = useActiveOutlet();
  const [selectedTicketId, setSelectedTicketId] = useState<string | null>(null);

  // Drop a selected ticket if we switch outlets — keeps the right pane sane.
  useEffect(() => {
    setSelectedTicketId(null);
  }, [outletId]);

  const subscribe = useMemo(() => {
    if (!tenantId || !outletId) return null;
    const repo = ticketsRepoFor(tenantId, outletId);
    return (cb: (vs: Ticket[]) => void) => repo.listSnapshot(cb);
  }, [tenantId, outletId]);

  const { data: tickets, loading } = useTrackedSnapshot<Ticket[]>(
    ['pos', tenantId, outletId],
    subscribe,
  );

  // Promotion name lookup — one-shot per cashier session. Used by the payment
  // sheet to label the per-promo lines that bookTicket stamped on the ticket
  // snapshot. Fails open: if the load errors we fall back to the bare promo ID.
  const [promotionsById, setPromotionsById] = useState<Record<string, Promotion>>({});
  useEffect(() => {
    if (!tenantId) return;
    let cancelled = false;
    void promotionsRepoFor(tenantId)
      .list()
      .then((promos) => {
        if (cancelled) return;
        const map: Record<string, Promotion> = {};
        for (const p of promos) map[p.id] = p;
        setPromotionsById(map);
      })
      .catch((e) => {
        logger.error('pos: promotions load failed', { error: String(e) });
      });
    return () => {
      cancelled = true;
    };
  }, [tenantId]);

  if (!tenantId) return <Screen body={t.t('dashboard.pos.noTenant')} />;
  if (outletLoading) return <PosSkeleton label={t.t('common.loading')} />;
  if (!outletId) return <Screen body={t.t('dashboard.pos.noOutlets')} />;
  if (loading) return <PosSkeleton label={t.t('common.loading')} />;

  const ready = (tickets ?? [])
    .filter((tk) => tk.status === 'readyForCollection')
    .sort((a, b) => {
      const ams = a.updatedAt?.toMillis?.() ?? 0;
      const bms = b.updatedAt?.toMillis?.() ?? 0;
      return ams - bms;
    });

  const selected = selectedTicketId
    ? (ready.find((tk) => tk.id === selectedTicketId) ?? null)
    : null;

  return (
    <main
      className="min-h-dvh"
      style={{
        background: 'var(--tint-subtle)',
        color: 'var(--color-on-surface)',
        padding: 'var(--space-6) var(--space-5)',
      }}
    >
      <header
        style={{
          marginBottom: 'var(--space-6)',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-end',
          gap: 'var(--space-4)',
        }}
      >
        <div>
          <h1 style={{ fontSize: 'var(--text-h1)', fontWeight: 600, margin: 0 }}>
            {t.t('dashboard.pos.title')}
          </h1>
          <p
            style={{
              fontSize: 'var(--text-meta)',
              color: 'var(--color-on-surface-muted)',
              margin: 0,
            }}
          >
            {t.t('dashboard.pos.readyCount', { count: ready.length })}
          </p>
        </div>
      </header>

      <div className="layout-split-lead">
        {/* Ready tickets list */}
        <section
          style={{
            background: 'var(--color-surface)',
            borderRadius: 'var(--radius-lg)',
            padding: 'var(--space-4)',
            boxShadow: 'var(--shadow-1)',
            minHeight: 320,
          }}
        >
          <h2 style={{ fontSize: 'var(--text-body)', fontWeight: 600, margin: 0 }}>
            {t.t('queueStatus.readyForCollection')}
          </h2>
          <div style={{ marginTop: 'var(--space-3)', display: 'grid', gap: 'var(--space-2)' }}>
            {ready.map((tk, i) => {
              const isSel = tk.id === selectedTicketId;
              // Sprint 111 — flag tickets the customer already settled via app
              // so the cashier can see at a glance "this one just needs to be
              // handed over" without opening the panel.
              const paidOnline = tk.payment?.recordedBy === '__system__';
              return (
                <button
                  key={tk.id}
                  type="button"
                  className="k-press k-rise"
                  onClick={() => {
                    feel('tap');
                    setSelectedTicketId(tk.id);
                  }}
                  aria-pressed={isSel}
                  style={{ ...ticketRowStyle(isSel), '--k-i': i } as CSSProperties}
                >
                  {/* The plate leads, big: it is what the cashier matches
                      against the car at the counter, in any language. */}
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      gap: 'var(--space-3)',
                    }}
                  >
                    <strong
                      style={{
                        fontSize: 'var(--text-h2)',
                        fontWeight: 800,
                        letterSpacing: '0.04em',
                        lineHeight: 1.1,
                        fontVariantNumeric: 'tabular-nums',
                        minWidth: 0,
                        overflowWrap: 'anywhere',
                      }}
                    >
                      {tk.snapshot.plate ?? `#${tk.number}`}
                    </strong>
                    <span
                      style={{
                        fontSize: 'var(--text-body)',
                        fontWeight: 700,
                        fontVariantNumeric: 'tabular-nums',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {money.format(tk.snapshot.total)}
                    </span>
                  </div>
                  <p
                    style={{
                      margin: `var(--space-1) 0 0 0`,
                      fontSize: 'var(--text-meta)',
                      color: 'var(--color-on-surface-muted)',
                      display: 'flex',
                      flexWrap: 'wrap',
                      alignItems: 'center',
                      gap: 'var(--space-2)',
                    }}
                  >
                    <span style={{ fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>
                      #{tk.number}
                    </span>
                    <span>{tk.snapshot.packageName ?? '—'}</span>
                    {paidOnline ? (
                      <span
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: 'var(--space-1)',
                          padding: '0 var(--space-2)',
                          borderRadius: 'var(--radius-pill)',
                          background: 'var(--color-success-bg)',
                          color: 'var(--color-success)',
                          fontWeight: 600,
                        }}
                      >
                        <CheckIcon size={14} />
                        {t.t('dashboard.pos.paidOnlineBadge')}
                      </span>
                    ) : null}
                  </p>
                </button>
              );
            })}
            {ready.length === 0 ? (
              <p
                style={{
                  fontSize: 'var(--text-meta)',
                  color: 'var(--color-on-surface-muted)',
                  textAlign: 'center',
                  margin: `var(--space-4) 0`,
                }}
              >
                {t.t('dashboard.pos.noTicketsWaiting')}
              </p>
            ) : null}
          </div>
        </section>

        {/* Payment sheet */}
        <section
          style={{
            background: 'var(--color-surface)',
            borderRadius: 'var(--radius-lg)',
            padding: 'var(--space-5)',
            boxShadow: 'var(--shadow-1)',
            minHeight: 320,
          }}
        >
          {selected ? (
            <PaymentSheet
              ticket={selected}
              tenantId={tenantId}
              outletId={outletId}
              locale={t.locale}
              promotionsById={promotionsById}
              onCompleted={() => setSelectedTicketId(null)}
            />
          ) : (
            <p style={{ color: 'var(--color-on-surface-muted)' }}>
              {t.t('dashboard.pos.selectTicketPrompt')}
            </p>
          )}
        </section>
      </div>
    </main>
  );
}

function PaymentSheet({
  ticket,
  tenantId,
  outletId,
  locale,
  promotionsById,
  onCompleted,
}: {
  readonly ticket: Ticket;
  readonly tenantId: string;
  readonly outletId: string;
  readonly locale: string;
  readonly promotionsById: Record<string, Promotion>;
  readonly onCompleted: () => void;
}) {
  const t = useI18n();
  const [method, setMethod] = useState<PaymentMethod>('cash');
  const [wallet, setWallet] = useState<GetTicketCustomerWalletOutput | null>(null);
  const [walletLoading, setWalletLoading] = useState(true);
  const [walletError, setWalletError] = useState<string | null>(null);
  const [selectedVoucherIds, setSelectedVoucherIds] = useState<Set<string>>(new Set());
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Load wallet whenever the selected ticket changes.
  useEffect(() => {
    setMethod('cash');
    setSelectedVoucherIds(new Set());
    setError(null);
    setWalletError(null);
    setWalletLoading(true);
    let cancelled = false;
    (async () => {
      try {
        const w = await callGetTicketCustomerWallet({
          tenantId,
          outletId,
          ticketId: ticket.id,
        });
        if (!cancelled) {
          setWallet(w);
          setWalletLoading(false);
        }
      } catch (e) {
        logger.error('getTicketCustomerWallet failed', { ticketId: ticket.id, error: String(e) });
        if (!cancelled) {
          setWallet(null);
          setWalletError(t.t('dashboard.pos.walletLoadError'));
          setWalletLoading(false);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
    // `t` is only used in the error-copy branch; adding it would needlessly
    // re-fetch the wallet on a locale switch. Copy resolves at next render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ticket.id, tenantId, outletId]);

  const tier: Tier | null = wallet?.tier ?? null;
  const activeVouchers = useMemo<readonly IssuedVoucher[]>(
    () => wallet?.vouchers ?? [],
    [wallet?.vouchers],
  );
  const selectedVouchers = useMemo(
    () => activeVouchers.filter((v) => selectedVoucherIds.has(v.id)),
    [activeVouchers, selectedVoucherIds],
  );

  // Sprint 96 — live-subscribe to this customer's active wash packs at this
  // tenant so the cashier sees "Redeem 1 wash" as a first-class shortcut
  // when an eligible pack exists. Filter is server-side via
  // `subscriptionsByCustomerSnapshot` so we never read the whole tenant
  // subscriptions collection. Walk-ins have no customerUid → no listener.
  //
  // Error policy: on listener error we surface an inline note and let
  // the cashier proceed with the standard payment-method chips —
  // silently hiding the redeem path would risk double-charging a
  // customer who pre-paid via a pack ([feedback-no-dead-ends]).
  const customerUid = ticket.customerUid;
  const [redeemableSubs, setRedeemableSubs] = useState<CustomerSubscription[]>([]);
  const [subsLookupError, setSubsLookupError] = useState<string | null>(null);
  useEffect(() => {
    if (!customerUid) {
      setRedeemableSubs([]);
      setSubsLookupError(null);
      return;
    }
    setSubsLookupError(null);
    const unsub = subscriptionsByCustomerSnapshot(
      tenantId,
      customerUid,
      (subs) => {
        // Filter to subs whose `checkRedemption` would approve for THIS
        // ticket's outlet + first service (=== snapshot.packageId — the
        // catalog package the customer booked). Same helper the server
        // runs, so what the cashier sees here is what the redeem call
        // will accept.
        const eligible = subs.filter((s) => {
          const verdict = checkRedemption({
            subscription: s,
            outletId,
            serviceId: ticket.snapshot.packageId ?? 'wash',
            ...(ticket.snapshot.vehicleType ? { vehicleTypeId: ticket.snapshot.vehicleType } : {}),
            nowMs: Date.now(),
          });
          return verdict.ok;
        });
        setRedeemableSubs(eligible);
        setSubsLookupError(null); // recovery
      },
      (err) => {
        logger.error('subscriptionsByCustomerSnapshot failed at POS', {
          tenantId,
          customerUid,
          ticketId: ticket.id,
          error: String(err),
        });
        // Don't blank `redeemableSubs` — last-known-good is safer than
        // silently hiding a pack the cashier already saw. But also
        // surface the warning so the cashier knows the listener is stale.
        setSubsLookupError(t.t('dashboard.pos.subsRefreshError'));
      },
    );
    return () => unsub();
    // `t` is only used in the error-copy branch; re-subscribing this Firestore
    // listener on a locale switch would cause a needless load flash.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    tenantId,
    outletId,
    customerUid,
    ticket.id,
    ticket.snapshot.packageId,
    ticket.snapshot.vehicleType,
  ]);

  // Promotions stamped on the ticket at bookTicket time. The snapshot stores
  // both the total savings (`promotionsApplied`) and the per-promo breakdown
  // (`appliedPromotions` — Sprint 58). Older tickets only have the IDs (Sprint
  // 56) or nothing at all (pre-Sprint 56). Each branch keeps the cashier
  // informed instead of silently swallowing the discount.
  const appliedPromotions = useMemo(() => {
    const snap = ticket.snapshot;
    if (snap.appliedPromotions && snap.appliedPromotions.length > 0) {
      return snap.appliedPromotions.map((ap) => {
        const promo = promotionsById[ap.id];
        return {
          id: ap.id,
          label: promo
            ? localeName(promo.name, locale)
            : t.t('dashboard.pos.promoLabel', { id: ap.id }),
          savings: ap.savingsSen,
        };
      });
    }
    // Back-compat path: Sprint-56 tickets have IDs + a single combined total.
    const ids = snap.appliedPromotionIds ?? [];
    const total = snap.promotionsApplied ?? 0;
    if (ids.length > 0 && total > 0) {
      // Split the total proportionally isn't faithful; surface a single combined
      // line that names every promo so the cashier still sees what applied.
      const label = ids
        .map((id) => {
          const promo = promotionsById[id];
          return promo ? localeName(promo.name, locale) : t.t('dashboard.pos.promoLabel', { id });
        })
        .join(' + ');
      return [{ id: ids.join('+'), label, savings: total }];
    }
    return [];
  }, [ticket.snapshot, promotionsById, locale, t]);

  const appliedPromotionIds = useMemo(
    () => (ticket.snapshot.appliedPromotionIds ?? []) as string[],
    [ticket.snapshot],
  );

  // Live breakdown — uses the same pure helper as the backend. Subtotal is
  // the PRE-promo amount (snapshot.subtotal). Promotions land as their own
  // discount band so the cashier sees `Subtotal · -Promo · -Tier · -Voucher
  // · Total` instead of a single mysterious "Total" different from the menu.
  const breakdown = useMemo(
    () =>
      priceWithDiscounts({
        subtotal: ticket.snapshot.subtotal,
        promotions: appliedPromotions,
        tier,
        tierLabel: tier ? localeName(tier.name, locale) : undefined,
        vouchers: selectedVouchers.map((v) => ({
          id: v.id,
          label: localeName(v.templateName, locale),
          effect: v.effect,
        })),
      }),
    [ticket.snapshot.subtotal, appliedPromotions, tier, selectedVouchers, locale],
  );

  /**
   * What the cashier actually asks for.
   *
   * `breakdown.total` is the price of the wash. It is NOT what is owed at the
   * bay when the customer has already paid a deposit to hold the slot, or holds
   * store credit with this operator — and the POS used to send exactly that to
   * `recordPayment`, so a customer who had prepaid RM 10.00 on a RM 25.00 wash
   * was asked for RM 25.00 again. RM 35.00 for a RM 25.00 wash, with the
   * overcollection recorded in `collectedDiff` where nobody looks.
   *
   * The server already subtracts both. This makes the number a human reads out
   * agree with the number the server records — which is the whole point of
   * having one figure.
   */
  const prepaidSen = ticket.prepaidSen ?? 0;
  const creditAvailableSen = wallet?.creditSen ?? 0;
  const afterPrepaid = Math.max(0, breakdown.total - prepaidSen);
  const creditToApply = Math.min(creditAvailableSen, afterPrepaid);
  const dueNow = Math.max(0, afterPrepaid - creditToApply);

  function toggleVoucher(id: string) {
    setSelectedVoucherIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  async function confirm() {
    setError(null);
    setSubmitting(true);
    haptic.tap();
    try {
      await callRecordPayment({
        tenantId,
        outletId,
        ticketId: ticket.id,
        method,
        // What the cashier collected — after the deposit and any credit, both of
        // which the server subtracts too. Sending the gross price here is what
        // overcharged a customer who had already paid a deposit.
        amount: dueNow,
        voucherIds: Array.from(selectedVoucherIds),
        promotionIds: appliedPromotionIds,
      });
      // Dopamine: confetti the operator's first paid ticket per outlet.
      // Keyed by (tenant, outlet) so each new outlet they spin up gets
      // its own celebration. localStorage single-fire guard inside
      // celebrateOnce — safe to call on every confirm.
      void celebrateOnce({
        key: `firstPaidTicket:${tenantId}:${outletId}`,
        intensity: 'milestone',
      }).then((fired) => {
        if (fired) haptic.celebration();
        else haptic.success();
      });
      onCompleted();
    } catch (err) {
      logger.error('recordPayment failed', { ticketId: ticket.id, error: String(err) });
      setError((err as Error).message ?? t.t('dashboard.pos.recordPaymentFailed'));
      setSubmitting(false);
    }
  }

  /**
   * Sprint 96 — redeem 1 wash from a customer's active pack, then close
   * out the ticket with `method: 'free'`. The redemption usage ledger
   * + the ticket's `redeemedFromSubscriptionId` stamp carry the full
   * audit trail (no need for a dedicated 'subscription' PaymentMethod
   * enum yet — see docs/next-session.md follow-up). Amount is 0 because
   * the customer paid when they bought the pack.
   */
  async function redeemAndComplete(sub: CustomerSubscription) {
    setError(null);
    setSubmitting(true);
    haptic.tap();
    try {
      await callRedeemWashFromSubscription({
        tenantId,
        subscriptionId: sub.id,
        ticketId: ticket.id,
        serviceId: ticket.snapshot.packageId ?? 'wash',
        outletId,
        ...(ticket.snapshot.vehicleType ? { vehicleTypeId: ticket.snapshot.vehicleType } : {}),
        notionalValueSen: ticket.snapshot.total,
      });
      await callRecordPayment({
        tenantId,
        outletId,
        ticketId: ticket.id,
        method: 'free',
        amount: 0,
        voucherIds: [],
        promotionIds: appliedPromotionIds,
        notes: `Redeemed from subscription ${sub.id} (${sub.packageSnapshot.name['en'] ?? 'wash pack'})`,
      });
      void celebrateOnce({
        key: `firstPaidTicket:${tenantId}:${outletId}`,
        intensity: 'milestone',
      }).then((fired) => {
        if (fired) haptic.celebration();
        else haptic.success();
      });
      onCompleted();
    } catch (err) {
      logger.error('redeem-and-complete failed', {
        ticketId: ticket.id,
        subId: sub.id,
        error: String(err),
      });
      const msg = (err as Error).message ?? t.t('dashboard.pos.redeemFailed');
      // Map the most common server-side refusal codes onto specific copy.
      if (msg.includes('failed-precondition')) {
        if (msg.includes('expired')) setError(t.t('dashboard.pos.redeemErrExpired'));
        else if (msg.includes('exhausted')) setError(t.t('dashboard.pos.redeemErrExhausted'));
        else if (msg.includes('wrong_outlet')) setError(t.t('dashboard.pos.redeemErrWrongOutlet'));
        else setError(t.t('dashboard.pos.redeemErrCovered'));
      } else if (msg.includes('permission-denied')) {
        setError(t.t('dashboard.pos.redeemErrNoPerm'));
      } else {
        setError(msg);
      }
      setSubmitting(false);
    }
  }

  // Sprint 111 — when the customer has already paid via the app, replace
  // the entire cash-recording UI with a "Paid online" panel. Single
  // primary action: mark collected (which archives the ticket via the
  // standard advanceTicket callable — no payment writes). Prevents the
  // double-pay hazard the server-side guard also enforces.
  const paidOnline = ticket.payment?.recordedBy === '__system__';

  return (
    <div style={{ display: 'grid', gap: 'var(--space-4)' }}>
      <div>
        <p
          style={{
            fontSize: 'var(--text-meta)',
            color: 'var(--color-on-surface-muted)',
            margin: 0,
          }}
        >
          {t.t('dashboard.pos.ticketHeader', {
            number: ticket.number,
            package: ticket.snapshot.packageName ?? '—',
          })}
        </p>
        <h2 style={{ fontSize: 'var(--text-h2)', fontWeight: 600, margin: `var(--space-1) 0 0 0` }}>
          {ticket.snapshot.plate ?? '—'}
        </h2>
      </div>

      {paidOnline ? (
        <PaidOnlinePanel
          ticket={ticket}
          tenantId={tenantId}
          outletId={outletId}
          onCompleted={onCompleted}
        />
      ) : (
        <>
          {/* Loyalty section: tier badge + voucher chips. Only renders when we have
          a wallet result (with or without vouchers — we still want to surface
          the tier auto-apply if it kicks in). */}
          {walletLoading ? (
            <p
              style={{
                fontSize: 'var(--text-meta)',
                color: 'var(--color-on-surface-muted)',
                margin: 0,
              }}
            >
              {t.t('dashboard.pos.loadingLoyalty')}
            </p>
          ) : (
            <LoyaltySection
              wallet={wallet}
              walletError={walletError}
              selectedVoucherIds={selectedVoucherIds}
              onToggleVoucher={toggleVoucher}
              tenantId={tenantId}
              locale={locale}
            />
          )}

          {redeemableSubs.length > 0 ? (
            <SubscriptionRedeemPanel
              subs={redeemableSubs}
              submitting={submitting}
              onRedeem={(s) => void redeemAndComplete(s)}
            />
          ) : null}

          {subsLookupError ? (
            <p
              role="alert"
              style={{
                margin: 0,
                padding: 'var(--space-3)',
                borderRadius: 'var(--radius-md)',
                background: 'var(--color-warn-bg)',
                border: '1px solid var(--color-warn)',
                fontSize: 'var(--text-meta)',
                color: 'var(--color-warn)',
              }}
            >
              {subsLookupError}
            </p>
          ) : null}

          {/* Sprint 119 — pre-payment upsell. Cashier appends add-ons to
          the ticket before recording payment. The breakdown below
          re-renders automatically via the live ticket subscription. */}
          <UpsellPanel
            ticket={ticket}
            tenantId={tenantId}
            outletId={outletId}
            disabled={submitting}
          />

          <fieldset style={{ border: 'none', padding: 0, margin: 0 }}>
            <legend
              style={{
                fontSize: 'var(--text-meta)',
                color: 'var(--color-on-surface-muted)',
                marginBottom: 'var(--space-2)',
              }}
            >
              {redeemableSubs.length > 0
                ? t.t('dashboard.pos.orChargeAnotherWay')
                : t.t('dashboard.pos.paymentMethodLegend')}
            </legend>
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(min(100%, 6.5rem), 1fr))',
                gap: 'var(--space-2)',
              }}
            >
              {PAYMENT_METHODS.map((pm) => {
                const sel = method === pm;
                const Icon = METHOD_ICON[pm];
                return (
                  <button
                    key={pm}
                    type="button"
                    className="k-press"
                    aria-pressed={sel}
                    onClick={() => {
                      feel('tap');
                      setMethod(pm);
                    }}
                    style={symbolTileStyle(sel)}
                  >
                    <Icon size={32} />
                    <span style={symbolTileLabelStyle}>
                      {t.t(`dashboard.pos.paymentMethods.${pm}`)}
                    </span>
                  </button>
                );
              })}
            </div>
          </fieldset>

          <Breakdown
            breakdown={breakdown}
            prepaidSen={prepaidSen}
            creditSen={creditToApply}
            dueNow={dueNow}
          />

          {error ? (
            <p
              role="alert"
              style={{ color: 'var(--color-accent)', fontSize: 'var(--text-meta)', margin: 0 }}
            >
              {error}
            </p>
          ) : null}

          {/* The amount IS the button: a big ✓ and the figure to collect,
              readable from across the counter. The accessible name keeps the
              full "Confirm RM 25.00" sentence. */}
          <button
            type="button"
            className="k-press"
            onClick={() => void confirm()}
            disabled={submitting}
            aria-busy={submitting}
            aria-label={
              submitting
                ? t.t('dashboard.pos.recording')
                : t.t('dashboard.pos.confirmAmount', { amount: money.format(dueNow) })
            }
            style={{
              background: 'var(--color-primary)',
              color: 'var(--color-primary-fg)',
              minHeight: 'max(4rem, var(--touch-target))',
              padding: 'var(--space-3) var(--space-5)',
              border: 'none',
              borderRadius: 'var(--radius-md)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 'var(--space-3)',
              cursor: submitting ? 'wait' : 'pointer',
              opacity: submitting ? 0.6 : 1,
              boxShadow: 'var(--shadow-2)',
              fontFamily: 'inherit',
            }}
          >
            {submitting ? (
              <>
                <Spinner size={24} />
                <span style={{ fontSize: 'var(--text-body)', fontWeight: 600 }}>
                  {t.t('dashboard.pos.recording')}
                </span>
              </>
            ) : (
              <>
                <span
                  aria-hidden
                  style={{
                    width: '2.5rem',
                    height: '2.5rem',
                    borderRadius: 'var(--radius-pill)',
                    background: 'color-mix(in oklab, var(--color-primary-fg) 20%, transparent)',
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                  }}
                >
                  <CheckIcon size={26} />
                </span>
                <span style={{ display: 'grid', textAlign: 'start', lineHeight: 1.1 }}>
                  <span style={{ fontSize: 'var(--text-meta)', fontWeight: 600, opacity: 0.85 }}>
                    {t.t('dashboard.pos.confirmShort')}
                  </span>
                  <span
                    style={{
                      fontSize: 'var(--text-h1)',
                      fontWeight: 800,
                      fontVariantNumeric: 'tabular-nums',
                      letterSpacing: '-0.01em',
                    }}
                  >
                    {money.format(dueNow)}
                  </span>
                </span>
              </>
            )}
          </button>
        </>
      )}
    </div>
  );
}

/**
 * Sprint 111 — PaidOnlinePanel.
 *
 * Renders in place of the entire payment-recording UI when a customer
 * has already settled this ticket via the app. Shows method + amount +
 * receivedAt + the toyyibPay refno notes so the cashier has the receipt
 * info they need; the only action is "Mark collected" which calls
 * `advanceTicket(to: 'collected')` and archives the ticket without
 * re-recording payment (server-side guard refuses any such attempt for
 * system-recorded payments, this just keeps the UI honest).
 */
/**
 * Sprint 119 — pre-payment upsell panel.
 *
 * Surfaces a `+ Add item` button on the payment sheet. When the cashier
 * taps it, expands inline to a picker that lists every active tenant-
 * library add-on visible at this outlet AND priced for the ticket's
 * current vehicle type, with anything already on the ticket greyed out.
 *
 * Picking one fires `callAppendItemsToTicket`. The live ticket
 * subscription in PaymentSheet picks up the snapshot change in
 * milliseconds — breakdown total re-renders without any local state
 * dance. Customer's /queue/{id} view updates too.
 *
 * State machine: idle → picker_open → submitting → success / error.
 * Error codes get mapped to actionable copy so the cashier sees the
 * exact reason ("Customer's mid-payment — wait or ask them to cancel"
 * for `payment_in_flight`, etc.). Always-visible "Close" so the picker
 * doesn't dead-end the cashier on a phone-screen tablet.
 */
function UpsellPanel({
  ticket,
  tenantId,
  outletId,
  disabled,
}: {
  readonly ticket: Ticket;
  readonly tenantId: string;
  readonly outletId: string;
  readonly disabled: boolean;
}) {
  const t = useI18n();
  const [open, setOpen] = useState(false);
  const [addOns, setAddOns] = useState<AddOn[] | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [lastAdded, setLastAdded] = useState<{
    name: string;
    price: number;
    addedTotal: number;
    newTotal: number;
  } | null>(null);

  const vehicleTypeId = ticket.snapshot.vehicleType ?? null;
  const onTicket = useMemo(
    () => new Set(ticket.snapshot.addOnIds ?? []),
    [ticket.snapshot.addOnIds],
  );

  // Lazy-load the catalog the first time the picker opens. Lives in
  // component state (not a global cache) since most cashier sessions
  // never trigger this and we'd rather not pay the read until needed.
  useEffect(() => {
    if (!open || addOns !== null) return;
    let cancelled = false;
    setLoadError(null);
    void addOnsRepoFor(tenantId)
      .list()
      .then((list) => {
        if (cancelled) return;
        // Outlet-visible + active only. Vehicle-type pricing filter
        // happens at render time so we can show "× not priced for SUV"
        // hints rather than silently dropping items.
        const scoped = filterForOutlet(list, outletId).filter((a) => a.active !== false);
        setAddOns(scoped);
      })
      .catch((err) => {
        if (cancelled) return;
        logger.error('UpsellPanel addOns load failed', { error: String(err) });
        setLoadError(t.t('dashboard.pos.upsellLoadError'));
      });
    return () => {
      cancelled = true;
    };
    // `t` is only used in the error-copy branch; adding it would needlessly
    // reload the add-on catalog on a locale switch. Copy resolves at next render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, addOns, tenantId, outletId]);

  async function append(addOnId: string, addOnName: string, price: number) {
    setSubmitting(true);
    setSubmitError(null);
    haptic.tap();
    try {
      const result = await callAppendItemsToTicket({
        tenantId,
        outletId,
        ticketId: ticket.id,
        addOnIds: [addOnId],
      });
      setLastAdded({
        name: addOnName,
        price,
        addedTotal: result.addedItemsTotal,
        newTotal: result.newTotalSen,
      });
      haptic.success();
    } catch (err) {
      const e = err as { code?: string; message?: string; details?: { code?: string } };
      const code = e.details?.code ?? e.code ?? '';
      const msg = e.message ?? '';
      const mapped =
        code === 'already_paid' || msg.includes('already_paid')
          ? t.t('dashboard.pos.upsellErrAlreadyPaid')
          : code === 'terminal_status' || msg.includes('terminal_status')
            ? t.t('dashboard.pos.upsellErrTerminal')
            : code === 'payment_in_flight' || msg.includes('payment_in_flight')
              ? t.t('dashboard.pos.upsellErrInFlight')
              : code === 'duplicate_add_on' || msg.includes('duplicate_add_on')
                ? t.t('dashboard.pos.upsellErrDuplicate')
                : code === 'add_on_excluded' || msg.includes('add_on_excluded')
                  ? t.t('dashboard.pos.upsellErrExcluded')
                  : code === 'add_on_unpriced' || msg.includes('add_on_unpriced')
                    ? t.t('dashboard.pos.upsellErrUnpriced')
                    : code === 'concurrent_amend' || msg.includes('concurrent_amend')
                      ? t.t('dashboard.pos.upsellErrConcurrent')
                      : msg || t.t('dashboard.pos.upsellErrGeneric');
      setSubmitError(mapped);
      logger.error('appendItemsToTicket failed', { code, error: msg });
    } finally {
      setSubmitting(false);
    }
  }

  if (!vehicleTypeId) {
    // Defensive: pre-Sprint-104 tickets without a vehicle type can't be
    // upsold. Surface a soft hint rather than a broken button.
    return (
      <p
        style={{
          fontSize: 'var(--text-meta)',
          color: 'var(--color-on-surface-muted)',
          margin: 0,
        }}
      >
        {t.t('dashboard.pos.upsellNoVehicleType')}
      </p>
    );
  }

  if (!open) {
    return (
      <div style={{ display: 'grid', gap: 'var(--space-2)' }}>
        <button
          type="button"
          onClick={() => setOpen(true)}
          disabled={disabled}
          style={{
            background: 'transparent',
            border: '1px dashed var(--border-medium, var(--color-border-strong))',
            borderRadius: 'var(--radius-md)',
            padding: '10px 14px',
            color: 'var(--color-primary)',
            fontSize: 'var(--text-meta)',
            fontWeight: 500,
            cursor: disabled ? 'not-allowed' : 'pointer',
            opacity: disabled ? 0.5 : 1,
            textAlign: 'left',
          }}
        >
          {t.t('dashboard.pos.upsellAddItem')}
        </button>
        {lastAdded ? (
          <p
            role="status"
            style={{
              margin: 0,
              padding: 'var(--space-2) var(--space-3)',
              background: 'rgba(13,122,79,0.06)',
              border: '1px solid rgba(13,122,79,0.22)',
              borderRadius: 'var(--radius-sm)',
              fontSize: 'var(--text-meta)',
              color: 'var(--color-primary)',
              fontVariantNumeric: 'tabular-nums',
            }}
          >
            {t.t('dashboard.pos.upsellAdded', {
              name: lastAdded.name,
              added: money.format(lastAdded.addedTotal),
              total: money.format(lastAdded.newTotal),
            })}
          </p>
        ) : null}
      </div>
    );
  }

  const list = addOns ?? [];
  return (
    <section
      aria-label={t.t('dashboard.pos.upsellSectionAria')}
      style={{
        background: 'var(--tint-subtle)',
        border: '1px solid var(--border-soft, var(--color-border))',
        borderRadius: 'var(--radius-md)',
        padding: 'var(--space-3) var(--space-4)',
        display: 'grid',
        gap: 'var(--space-3)',
      }}
    >
      <header
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'baseline',
          gap: 'var(--space-2)',
        }}
      >
        <h3 style={{ margin: 0, fontSize: 'var(--text-body)', fontWeight: 600 }}>
          {t.t('dashboard.pos.upsellHeading')}
        </h3>
        <button
          type="button"
          onClick={() => {
            setOpen(false);
            setSubmitError(null);
          }}
          style={{
            background: 'transparent',
            border: 'none',
            color: 'var(--color-on-surface-muted)',
            fontSize: 'var(--text-meta)',
            cursor: 'pointer',
            padding: '4px 8px',
          }}
        >
          {t.t('dashboard.pos.close')}
        </button>
      </header>

      {addOns === null && !loadError ? (
        <p
          style={{
            margin: 0,
            fontSize: 'var(--text-meta)',
            color: 'var(--color-on-surface-muted)',
          }}
        >
          {t.t('dashboard.pos.loadingAddOns')}
        </p>
      ) : loadError ? (
        <p
          role="alert"
          style={{ margin: 0, fontSize: 'var(--text-meta)', color: 'var(--color-accent)' }}
        >
          {loadError}
        </p>
      ) : list.length === 0 ? (
        <p
          style={{
            margin: 0,
            fontSize: 'var(--text-meta)',
            color: 'var(--color-on-surface-muted)',
          }}
        >
          {t.t('dashboard.pos.upsellNoneConfiguredPre')}
          <code>{t.t('dashboard.pos.upsellCatalogPath')}</code>
          {t.t('dashboard.pos.upsellNoneConfiguredPost')}
        </p>
      ) : (
        <ul
          style={{
            listStyle: 'none',
            margin: 0,
            padding: 0,
            display: 'grid',
            gap: 'var(--space-2)',
          }}
        >
          {list.map((ad) => {
            const onIt = onTicket.has(ad.id);
            const price = ad.pricingByVehicleType[vehicleTypeId];
            const unpriced = price === undefined;
            const disabledRow = onIt || unpriced || submitting;
            const reason = onIt
              ? t.t('dashboard.pos.upsellAlreadyAdded')
              : unpriced
                ? t.t('dashboard.pos.upsellNotPriced', {
                    vehicleType:
                      ticket.snapshot.vehicleType ?? t.t('dashboard.pos.thisVehicleType'),
                  })
                : null;
            return (
              <li
                key={ad.id}
                style={{
                  display: 'grid',
                  gridTemplateColumns: '1fr auto auto',
                  gap: 'var(--space-3)',
                  alignItems: 'baseline',
                  padding: 'var(--space-2) var(--space-3)',
                  background: 'var(--color-surface)',
                  borderRadius: 'var(--radius-sm)',
                  border: '1px solid var(--border-faint, var(--color-border))',
                  opacity: disabledRow && !submitting ? 0.6 : 1,
                }}
              >
                <div style={{ minWidth: 0 }}>
                  <p style={{ margin: 0, fontWeight: 500 }}>{ad.name['en'] ?? ad.id}</p>
                  {reason ? (
                    <p
                      style={{
                        margin: '2px 0 0 0',
                        fontSize: 'var(--text-meta)',
                        color: 'var(--color-on-surface-muted)',
                      }}
                    >
                      {reason}
                    </p>
                  ) : null}
                </div>
                <span style={{ fontVariantNumeric: 'tabular-nums', fontSize: 'var(--text-meta)' }}>
                  {unpriced ? '—' : `+${money.format(price)}`}
                </span>
                <button
                  type="button"
                  disabled={disabledRow}
                  onClick={() => void append(ad.id, ad.name['en'] ?? ad.id, price ?? 0)}
                  style={{
                    background: disabledRow ? 'transparent' : 'var(--color-primary)',
                    color: disabledRow
                      ? 'var(--color-on-surface-muted)'
                      : 'var(--color-primary-fg)',
                    padding: '6px 12px',
                    borderRadius: 'var(--radius-sm)',
                    border: disabledRow
                      ? '1px solid var(--border-soft, var(--color-border))'
                      : 'none',
                    fontSize: 'var(--text-meta)',
                    fontWeight: 500,
                    cursor: disabledRow ? 'not-allowed' : 'pointer',
                  }}
                >
                  {submitting
                    ? '…'
                    : onIt
                      ? t.t('dashboard.pos.upsellOnTicket')
                      : t.t('dashboard.pos.upsellAdd')}
                </button>
              </li>
            );
          })}
        </ul>
      )}

      {submitError ? (
        <p
          role="alert"
          style={{
            margin: 0,
            padding: 'var(--space-2) var(--space-3)',
            background: 'rgba(245,158,11,0.10)',
            border: '1px solid rgba(245,158,11,0.30)',
            borderRadius: 'var(--radius-sm)',
            fontSize: 'var(--text-meta)',
            color: '#92400e',
            lineHeight: 1.4,
          }}
        >
          {submitError}
        </p>
      ) : null}
    </section>
  );
}

function PaidOnlinePanel({
  ticket,
  tenantId,
  outletId,
  onCompleted,
}: {
  readonly ticket: Ticket;
  readonly tenantId: string;
  readonly outletId: string;
  readonly onCompleted: () => void;
}) {
  const t = useI18n();
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const payment = ticket.payment;
  const alreadyCollected = ticket.status === 'collected';
  if (!payment) return null; // upstream gate ensures payment exists

  async function markCollected() {
    setError(null);
    setSubmitting(true);
    haptic.tap();
    try {
      await callAdvanceTicket({
        tenantId,
        outletId,
        ticketId: ticket.id,
        to: 'collected',
      });
      void celebrateOnce({
        key: `firstPaidTicket:${tenantId}:${outletId}`,
        intensity: 'milestone',
      }).then((fired) => {
        if (fired) haptic.celebration();
        else haptic.success();
      });
      onCompleted();
    } catch (err) {
      logger.error('advanceTicket→collected failed (paid-online)', {
        ticketId: ticket.id,
        error: String(err),
      });
      const msg = (err as Error).message ?? t.t('dashboard.pos.paidCloseFailed');
      setError(msg);
      setSubmitting(false);
    }
  }

  const receivedDate = payment.receivedAt.toDate?.() ?? null;
  const receivedLabel = receivedDate
    ? receivedDate.toLocaleString('en-GB', {
        hour: '2-digit',
        minute: '2-digit',
        day: '2-digit',
        month: 'short',
      })
    : t.t('dashboard.pos.paidReceivedMomentAgo');

  return (
    <div style={{ display: 'grid', gap: 'var(--space-4)' }}>
      <div
        style={{
          padding: 'var(--space-4) var(--space-5)',
          borderRadius: 'var(--radius-lg)',
          background: 'rgba(13,122,79,0.08)',
          border: '1px solid rgba(13,122,79,0.22)',
          display: 'grid',
          gap: 'var(--space-2)',
        }}
      >
        <p
          style={{
            margin: 0,
            fontWeight: 600,
            color: 'var(--color-primary)',
            fontSize: 'var(--text-body)',
          }}
        >
          {t.t('dashboard.pos.paidOnlineAmount', { amount: money.format(payment.amount) })}
        </p>
        <p
          style={{
            margin: 0,
            fontSize: 'var(--text-meta)',
            color: 'var(--color-on-surface-muted)',
            lineHeight: 1.5,
          }}
        >
          {t.t('dashboard.pos.paidExplainer', { time: receivedLabel })}
        </p>
        {payment.notes ? (
          <p
            style={{
              margin: 0,
              padding: 'var(--space-2) var(--space-3)',
              borderRadius: 'var(--radius-sm)',
              background: 'var(--tint-subtle)',
              fontSize: 'var(--text-meta)',
              fontFamily: 'var(--font-mono, monospace)',
              color: 'var(--color-on-surface-muted)',
              wordBreak: 'break-all',
            }}
          >
            {payment.notes}
          </p>
        ) : null}
      </div>

      {error ? (
        <p
          role="alert"
          style={{ color: 'var(--color-accent)', fontSize: 'var(--text-meta)', margin: 0 }}
        >
          {error}
        </p>
      ) : null}

      {alreadyCollected ? (
        <p
          style={{
            fontSize: 'var(--text-meta)',
            color: 'var(--color-on-surface-muted)',
            margin: 0,
          }}
        >
          {t.t('dashboard.pos.paidAlreadyHistory')}
        </p>
      ) : (
        <button
          type="button"
          onClick={() => void markCollected()}
          disabled={submitting}
          style={{
            background: 'var(--color-primary)',
            color: 'var(--color-primary-fg)',
            padding: '14px 22px',
            border: 'none',
            borderRadius: 'var(--radius-md)',
            fontWeight: 500,
            fontSize: 'var(--text-body)',
            cursor: submitting ? 'wait' : 'pointer',
            opacity: submitting ? 0.6 : 1,
            boxShadow: 'var(--shadow-2)',
          }}
        >
          {submitting ? t.t('dashboard.pos.closingOut') : t.t('dashboard.pos.markCollected')}
        </button>
      )}
    </div>
  );
}

function LoyaltySection({
  wallet,
  walletError,
  selectedVoucherIds,
  onToggleVoucher,
  tenantId,
  locale,
}: {
  readonly wallet: GetTicketCustomerWalletOutput | null;
  readonly walletError: string | null;
  readonly selectedVoucherIds: ReadonlySet<string>;
  readonly onToggleVoucher: (id: string) => void;
  readonly tenantId: string;
  readonly locale: string;
}) {
  const t = useI18n();
  if (walletError) {
    return (
      <p
        role="alert"
        style={{ fontSize: 'var(--text-meta)', color: 'var(--color-accent)', margin: 0 }}
      >
        {walletError}
      </p>
    );
  }
  if (!wallet) return null;
  if (wallet.customerUid === null) {
    return (
      <p
        style={{ fontSize: 'var(--text-meta)', color: 'var(--color-on-surface-muted)', margin: 0 }}
      >
        {t.t('dashboard.pos.walkInNoLoyalty')}
      </p>
    );
  }
  const tier = wallet.tier;
  const vouchers = wallet.vouchers;
  const hasTier = tier !== null;
  const hasVouchers = vouchers.length > 0;
  if (!hasTier && !hasVouchers) {
    return (
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 'var(--space-3)',
        }}
      >
        <p
          style={{
            fontSize: 'var(--text-meta)',
            color: 'var(--color-on-surface-muted)',
            margin: 0,
          }}
        >
          {t.t('dashboard.pos.noTierNoVouchers')}
        </p>
        <IssueVoucherButton tenantId={tenantId} customerUid={wallet.customerUid} locale={locale} />
      </div>
    );
  }

  return (
    <div style={{ display: 'grid', gap: 'var(--space-3)' }}>
      {hasTier ? (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 'var(--space-2)',
          }}
        >
          <span
            aria-hidden
            style={{
              width: 10,
              height: 10,
              borderRadius: 'var(--radius-pill)',
              background: tier.badge.colour,
              display: 'inline-block',
            }}
          />
          <span style={{ fontSize: 'var(--text-meta)', color: 'var(--color-on-surface-muted)' }}>
            {t.t('dashboard.pos.tierAutoApplied', { tier: localeName(tier.name, locale) })}
          </span>
        </div>
      ) : null}

      {hasVouchers ? (
        <fieldset style={{ border: 'none', padding: 0, margin: 0 }}>
          <legend
            style={{
              fontSize: 'var(--text-meta)',
              color: 'var(--color-on-surface-muted)',
              marginBottom: 'var(--space-2)',
            }}
          >
            {t.t('dashboard.pos.vouchersLegend')}
          </legend>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--space-2)' }}>
            {vouchers.map((v) => {
              const sel = selectedVoucherIds.has(v.id);
              return (
                <button
                  key={v.id}
                  type="button"
                  aria-pressed={sel}
                  onClick={() => onToggleVoucher(v.id)}
                  style={chipStyle(sel)}
                >
                  {localeName(v.templateName, locale)}
                </button>
              );
            })}
          </div>
        </fieldset>
      ) : null}

      <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
        <IssueVoucherButton tenantId={tenantId} customerUid={wallet.customerUid} locale={locale} />
      </div>
    </div>
  );
}

function IssueVoucherButton({
  tenantId,
  customerUid,
  locale,
}: {
  readonly tenantId: string;
  readonly customerUid: string;
  readonly locale: string;
}) {
  const t = useI18n();
  const [open, setOpen] = useState(false);
  const [templates, setTemplates] = useState<VoucherTemplate[]>([]);
  const [loadingTemplates, setLoadingTemplates] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successId, setSuccessId] = useState<string | null>(null);

  function openSheet() {
    setOpen(true);
    setError(null);
    setSuccessId(null);
    setLoadingTemplates(true);
    voucherTemplatesRepoFor(tenantId)
      .list()
      .then((list) => {
        const now = Date.now();
        const eligible = list.filter((tmpl) => {
          if (!tmpl.active) return false;
          const expMs = tmpl.validity.to.toMillis?.() ?? tmpl.validity.to.seconds * 1000;
          return expMs > now;
        });
        setTemplates(eligible);
      })
      .catch((e) => {
        logger.error('templates load failed', { error: String(e) });
        setError(t.t('dashboard.pos.issueVoucherLoadError'));
      })
      .finally(() => setLoadingTemplates(false));
  }

  async function issue(templateId: string) {
    setSubmitting(true);
    setError(null);
    try {
      const out = await callIssueVoucher({ tenantId, templateId, customerUid });
      setSuccessId(out.voucherId);
    } catch (e) {
      logger.error('issueVoucher failed', { error: String(e) });
      setError((e as Error).message ?? t.t('dashboard.pos.issueVoucherFailed'));
    } finally {
      setSubmitting(false);
    }
  }

  if (!open) {
    return (
      <button
        type="button"
        onClick={openSheet}
        style={{
          background: 'transparent',
          color: 'var(--color-on-surface-muted)',
          border: '1px solid var(--color-border-strong)',
          padding: '6px 10px',
          borderRadius: 'var(--radius-sm)',
          fontSize: 'var(--text-meta)',
          cursor: 'pointer',
        }}
      >
        {t.t('dashboard.pos.issueVoucher')}
      </button>
    );
  }

  return (
    <div
      style={{
        width: '100%',
        padding: 'var(--space-3)',
        borderRadius: 'var(--radius-md)',
        border: '1px solid var(--color-border-strong)',
        background: 'var(--tint-subtle)',
        display: 'grid',
        gap: 'var(--space-2)',
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
        <strong style={{ fontSize: 'var(--text-meta)' }}>
          {t.t('dashboard.pos.issueVoucherTitle')}
        </strong>
        <button
          type="button"
          onClick={() => setOpen(false)}
          style={{
            background: 'transparent',
            border: 'none',
            color: 'var(--color-on-surface-muted)',
            fontSize: 'var(--text-meta)',
            cursor: 'pointer',
          }}
        >
          {t.t('dashboard.pos.close')}
        </button>
      </div>
      {loadingTemplates ? (
        <p
          style={{
            fontSize: 'var(--text-meta)',
            color: 'var(--color-on-surface-muted)',
            margin: 0,
          }}
        >
          {t.t('common.loading')}
        </p>
      ) : templates.length === 0 ? (
        <p
          style={{
            fontSize: 'var(--text-meta)',
            color: 'var(--color-on-surface-muted)',
            margin: 0,
          }}
        >
          {t.t('dashboard.pos.noActiveTemplatesVouchers')}
        </p>
      ) : (
        <div style={{ display: 'grid', gap: 'var(--space-1)' }}>
          {templates.map((tmpl) => (
            <button
              key={tmpl.id}
              type="button"
              disabled={submitting}
              onClick={() => void issue(tmpl.id)}
              style={{
                background: 'var(--color-surface)',
                border: '1px solid var(--color-border)',
                padding: '8px 10px',
                borderRadius: 'var(--radius-sm)',
                textAlign: 'left',
                cursor: submitting ? 'wait' : 'pointer',
                fontSize: 'var(--text-meta)',
              }}
            >
              {localeName(tmpl.name, locale)}
            </button>
          ))}
        </div>
      )}
      {error ? (
        <p
          role="alert"
          style={{ color: 'var(--color-accent)', fontSize: 'var(--text-meta)', margin: 0 }}
        >
          {error}
        </p>
      ) : null}
      {successId ? (
        <p style={{ color: 'var(--color-primary)', fontSize: 'var(--text-meta)', margin: 0 }}>
          {t.t('dashboard.pos.voucherIssuedWallet')}
        </p>
      ) : null}
    </div>
  );
}

/**
 * The price breakdown the cashier reads out.
 *
 *  and  are shown as their own lines rather than folded
 * into the total, because a total that silently differs from the menu price is
 * the thing an operator cannot defend when a customer questions it. "RM 25.00,
 * less RM 10.00 deposit, less RM 10.00 credit, RM 5.00 now" is answerable.
 */
function Breakdown({
  breakdown,
  prepaidSen,
  creditSen,
  dueNow,
}: {
  readonly breakdown: ReturnType<typeof priceWithDiscounts>;
  readonly prepaidSen: number;
  readonly creditSen: number;
  readonly dueNow: number;
}) {
  const t = useI18n();
  return (
    <div
      style={{
        background: 'var(--tint-subtle)',
        borderRadius: 'var(--radius-md)',
        padding: 'var(--space-3) var(--space-4)',
        display: 'grid',
        gap: 'var(--space-1)',
      }}
    >
      <Row label={t.t('dashboard.pos.subtotal')} value={money.format(breakdown.subtotal)} muted />
      {breakdown.discountLines.map((line) => (
        <Row
          key={line.id}
          label={t.t('dashboard.pos.discountLine', { label: line.label })}
          value={`− ${money.format(line.amount)}`}
          muted
        />
      ))}
      <div
        style={{
          marginTop: 'var(--space-2)',
          paddingTop: 'var(--space-2)',
          borderTop: '1px solid var(--color-border)',
        }}
      >
        <Row label={t.t('dashboard.pos.total')} value={money.format(breakdown.total)} muted />
        {prepaidSen > 0 ? (
          <Row label={t.t('dashboard.pos.prepaid')} value={`− ${money.format(prepaidSen)}`} muted />
        ) : null}
        {creditSen > 0 ? (
          <Row
            label={t.t('dashboard.pos.creditApplied')}
            value={`− ${money.format(creditSen)}`}
            muted
          />
        ) : null}
        <Row label={t.t('dashboard.pos.dueNow')} value={money.format(dueNow)} bold />
      </div>
    </div>
  );
}

/**
 * Sprint 96 — Subscription redemption affordance.
 *
 * Rendered above the standard payment-method chips ONLY when the customer
 * has at least one active wash pack whose `checkRedemption` verdict is
 * `ok` for this ticket. Each row is its own primary CTA — one tap = one
 * redemption call + ticket-paid record, no separate confirm needed.
 *
 * If multiple packs are eligible (rare but possible: e.g. an unlimited
 * monthly plus a 5-wash count), each gets its own button so the operator
 * picks which to debit. Most operators will only ever have one.
 */
function SubscriptionRedeemPanel({
  subs,
  submitting,
  onRedeem,
}: {
  readonly subs: readonly CustomerSubscription[];
  readonly submitting: boolean;
  readonly onRedeem: (sub: CustomerSubscription) => void;
}) {
  const t = useI18n();
  return (
    <div
      style={{
        padding: 'var(--space-4)',
        borderRadius: 'var(--radius-md)',
        background: 'rgba(13,122,79,0.06)',
        border: '1px solid rgba(13,122,79,0.22)',
        display: 'grid',
        gap: 'var(--space-2)',
      }}
    >
      <p
        style={{
          margin: 0,
          fontSize: 'var(--text-meta)',
          color: 'var(--color-primary)',
          fontWeight: 600,
          letterSpacing: '0.02em',
          textTransform: 'uppercase',
        }}
      >
        {t.t('dashboard.pos.activeWashPack')}
      </p>
      {subs.map((s) => {
        const remainingLabel =
          s.packageSnapshot.kind === 'unlimited'
            ? t.t('dashboard.pos.redeemUnlimited')
            : t.t('dashboard.pos.redeemRemainingCount', {
                remaining: s.washesRemaining,
                total: s.packageSnapshot.washCount ?? 0,
              });
        return (
          <button
            key={s.id}
            type="button"
            onClick={() => onRedeem(s)}
            disabled={submitting}
            style={{
              background: 'var(--color-primary)',
              color: 'var(--color-primary-fg)',
              padding: '12px 16px',
              borderRadius: 'var(--radius-md)',
              border: 'none',
              cursor: submitting ? 'wait' : 'pointer',
              opacity: submitting ? 0.6 : 1,
              textAlign: 'left',
              display: 'grid',
              gap: 2,
              boxShadow: 'var(--shadow-1)',
            }}
          >
            <span style={{ fontWeight: 600, fontSize: 'var(--text-body)' }}>
              {submitting
                ? t.t('dashboard.pos.redeeming')
                : t.t('dashboard.pos.redeemFromPack', {
                    pack: s.packageSnapshot.name['en'] ?? t.t('dashboard.pos.packFallback'),
                  })}
            </span>
            <span style={{ fontSize: 'var(--text-meta)', opacity: 0.9 }}>
              {t.t('dashboard.pos.redeemNoPayment', { remaining: remainingLabel })}
            </span>
          </button>
        );
      })}
    </div>
  );
}

function Row({
  label,
  value,
  muted,
  bold,
}: {
  readonly label: string;
  readonly value: string;
  readonly muted?: boolean;
  readonly bold?: boolean;
}) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
      <span
        style={{
          fontSize: 'var(--text-meta)',
          color: muted ? 'var(--color-on-surface-muted)' : 'var(--color-on-surface)',
          fontWeight: bold ? 600 : 400,
        }}
      >
        {label}
      </span>
      <span
        style={{
          fontVariantNumeric: 'tabular-nums',
          fontSize: bold ? 'var(--text-body)' : 'var(--text-meta)',
          color: muted ? 'var(--color-on-surface-muted)' : 'var(--color-on-surface)',
          fontWeight: bold ? 600 : 400,
        }}
      >
        {value}
      </span>
    </div>
  );
}

function localeName(name: Readonly<Record<string, string | undefined>>, locale: string): string {
  return name[locale] ?? name['en'] ?? '—';
}

function ticketRowStyle(selected: boolean): React.CSSProperties {
  return {
    display: 'block',
    width: '100%',
    textAlign: 'left',
    padding: 'var(--space-3) var(--space-4)',
    minHeight: 'max(4rem, var(--touch-target))',
    borderRadius: 'var(--radius-md)',
    border: `2px solid ${selected ? 'var(--color-primary)' : 'var(--color-border)'}`,
    background: selected ? 'var(--color-primary-soft)' : 'var(--color-surface)',
    cursor: 'pointer',
    color: 'inherit',
    font: 'inherit',
  };
}

/** A payment method as a big symbol tile, label beneath. */
/**
 * Layout-matched loading state for the cashier flow — mirrors the header +
 * two-pane (ready-tickets list · payment sheet) grid so the shell holds its
 * shape while data resolves. Shimmer via the shared `@kilat/ui` Skeleton;
 * panes stagger in with `k-rise`. No visible text — `label` is SR-only.
 */
function PosSkeleton({ label }: { readonly label: string }) {
  return (
    <main
      className="min-h-dvh"
      style={{
        background: 'var(--tint-subtle)',
        color: 'var(--color-on-surface)',
        padding: 'var(--space-6) var(--space-5)',
      }}
    >
      <span role="status" aria-live="polite" style={posSrOnly}>
        {label}
      </span>
      <header
        aria-hidden
        style={{
          marginBottom: 'var(--space-6)',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-end',
          gap: 'var(--space-4)',
        }}
      >
        <div style={{ display: 'grid', gap: 'var(--space-2)' }}>
          <Skeleton width={140} height={28} />
          <Skeleton width={110} height={14} />
        </div>
        <Skeleton width={120} height={36} radius="var(--radius-md)" />
      </header>
      <div aria-hidden className="layout-split-lead">
        <section
          className="k-rise"
          style={
            {
              background: 'var(--color-surface)',
              borderRadius: 'var(--radius-lg)',
              padding: 'var(--space-4)',
              boxShadow: 'var(--shadow-1)',
              minHeight: 320,
              display: 'grid',
              gap: 'var(--space-3)',
              alignContent: 'start',
              '--k-i': 0,
            } as CSSProperties
          }
        >
          <Skeleton width={150} height={16} />
          <div style={{ display: 'grid', gap: 'var(--space-2)' }}>
            {[0, 1, 2, 3].map((i) => (
              <div
                key={i}
                style={{
                  padding: 'var(--space-3)',
                  borderRadius: 'var(--radius-md)',
                  border: '1px solid var(--color-border)',
                  display: 'grid',
                  gap: 'var(--space-2)',
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Skeleton width={48} height={16} />
                  <Skeleton width={56} height={16} />
                </div>
                <Skeleton width="75%" height={12} />
              </div>
            ))}
          </div>
        </section>
        <section
          className="k-rise"
          style={
            {
              background: 'var(--color-surface)',
              borderRadius: 'var(--radius-lg)',
              padding: 'var(--space-5)',
              boxShadow: 'var(--shadow-1)',
              minHeight: 320,
              display: 'grid',
              gap: 'var(--space-4)',
              alignContent: 'start',
              '--k-i': 1,
            } as CSSProperties
          }
        >
          <div style={{ display: 'grid', gap: 'var(--space-2)' }}>
            <Skeleton width={120} height={12} />
            <Skeleton width={180} height={26} />
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--space-2)' }}>
            {[0, 1, 2, 3, 4].map((i) => (
              <Skeleton key={i} width={72} height={38} radius="var(--radius-pill)" />
            ))}
          </div>
          <Skeleton height={84} radius="var(--radius-md)" />
          <Skeleton height={48} radius="var(--radius-md)" />
        </section>
      </div>
    </main>
  );
}

function Screen({ body }: { readonly body: string }) {
  return (
    <main className="min-h-dvh flex items-center justify-center p-8 text-center">
      <p style={{ fontSize: 'var(--text-meta)', color: 'var(--color-on-surface-muted)' }}>{body}</p>
    </main>
  );
}
